import {
    ApiShopperLandingPageLink,
    VLPVariant,
} from '@storefronts/common-types'
import { Cart, VariantWithImage } from '../types'
import { runWhenReady } from '../components/pixels/pixelRuntime'

declare global {
    interface Window {
        axon?: (...args: any[]) => void
    }
}

const CURRENCY = 'USD'

interface AxonItem {
    item_id: string
    image_url?: string
    item_name?: string
    item_variant_id?: string
    price?: number
    quantity?: number
    discount?: number
}

interface AxonPageViewData {
    user_data?: Record<string, unknown>[]
}

interface AxonItemsEventData {
    items: AxonItem[]
    currency: string
    value?: number
    user_data?: Record<string, unknown>[]
}

interface AxonBeginCheckoutData {
    currency: string
    items: AxonItem[]
    value: number
    user_data?: Record<string, unknown>[]
}

const isAxonAvailable = (): boolean => {
    return typeof window !== 'undefined' && typeof window.axon === 'function'
}

const toDollars = (cents: number): number => {
    return Math.round(cents) / 100
}

const calculateDiscount = (
    compareAtPrice: number | null | undefined,
    actualPrice: number,
): number | undefined => {
    if (compareAtPrice && compareAtPrice > actualPrice) {
        return toDollars(compareAtPrice - actualPrice)
    }
    return undefined
}

const variantToAxonItem = (
    variant: VariantWithImage,
    quantity: number,
): AxonItem => {
    const item: AxonItem = {
        item_id: variant.link.shopifyId,
        item_variant_id: variant.shopifyId,
        item_name: variant.link.title,
        image_url: variant.imageUrl || variant.link.imageUrl,
        price: toDollars(variant.finalPrice),
        quantity,
    }

    const discount = calculateDiscount(
        variant.price.compareAtPrice,
        variant.finalPrice,
    )
    if (discount !== undefined) {
        item.discount = discount
    }

    return item
}

const linkToAxonItem = (
    link: ApiShopperLandingPageLink,
    variant?: VLPVariant | null,
): AxonItem => {
    const unitPriceCents = variant?.price.price ?? link.price.price
    const compareAtCents =
        variant?.price.compareAtPrice ?? link.price.compareAtPrice

    const item: AxonItem = {
        item_id: link.shopifyId,
        item_name: link.title,
        image_url: variant?.featuredImage ?? link.imageUrl,
        price: toDollars(unitPriceCents),
        quantity: 1,
    }

    if (variant) {
        item.item_variant_id = variant.shopifyId
    }

    const discount = calculateDiscount(compareAtCents, unitPriceCents)
    if (discount !== undefined) {
        item.discount = discount
    }

    return item
}

const calculateItemsValue = (items: AxonItem[]): number => {
    const total = items.reduce(
        (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
        0,
    )
    return Math.round(total * 100) / 100
}

export const AXON_PAGE_VIEW = (): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return
        const eventData: AxonPageViewData = {}
        window.axon!('track', 'page_view', eventData)
    })
}

export const AXON_VIEW_ITEM = (
    product: ApiShopperLandingPageLink,
    eventId?: string,
    variant?: VLPVariant | null,
): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return

        const items = [linkToAxonItem(product, variant)]
        const eventData: AxonItemsEventData = {
            items,
            currency: CURRENCY,
            value: calculateItemsValue(items),
        }
        if (eventId) {
            window.axon!('track', 'view_item', eventData, {
                dedupe_id: eventId,
            })
            return
        }
        window.axon!('track', 'view_item', eventData)
    })
}

export const AXON_ADD_TO_CART = (
    variant: VariantWithImage,
    quantity: number,
): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return

        const items = [variantToAxonItem(variant, quantity)]
        const value = calculateItemsValue(items)

        const eventData: AxonItemsEventData = {
            items,
            currency: CURRENCY,
            value,
        }
        window.axon!('track', 'add_to_cart', eventData)
    })
}

export const AXON_BEGIN_CHECKOUT = (cart: Cart): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return
        if (!cart.items.length) return

        sendBeginCheckout(
            cart.items.map((item) =>
                variantToAxonItem(item.variant, item.quantity),
            ),
        )
    })
}

export const AXON_BEGIN_CHECKOUT_BUY_NOW = (
    variant: VariantWithImage,
): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return
        sendBeginCheckout([variantToAxonItem(variant, 1)])
    })
}

const sendBeginCheckout = (items: AxonItem[]): void => {
    const eventData: AxonBeginCheckoutData = {
        currency: CURRENCY,
        items,
        value: calculateItemsValue(items),
    }
    window.axon!('track', 'begin_checkout', eventData)
}

export const AXON_VIEW_CART = (cart: Cart): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return
        if (!cart.items.length) return

        const items = cart.items.map((item) =>
            variantToAxonItem(item.variant, item.quantity),
        )
        const eventData: AxonItemsEventData = {
            items,
            currency: CURRENCY,
            value: calculateItemsValue(items),
        }
        window.axon!('track', 'view_cart', eventData)
    })
}

export const AXON_REMOVE_FROM_CART = (
    variant: VariantWithImage,
    quantity: number,
): void => {
    runWhenReady('axon', () => {
        if (!isAxonAvailable()) return

        const items = [variantToAxonItem(variant, quantity)]
        const eventData: AxonItemsEventData = {
            items,
            currency: CURRENCY,
            value: calculateItemsValue(items),
        }
        window.axon!('track', 'remove_from_cart', eventData)
    })
}
